/* eslint-disable react/no-children-prop */
import React from "react";
import { useTranslation } from "react-i18next";
// UI
import { notifications } from "@mantine/notifications";
import { Box, Fieldset, Group } from "@mantine/core";
import { useAppForm } from "@components/form/app-form";
// API
import { usePatchPlayerMutation } from "@src/services/apiQueries/players";
import { extractMaybeApiErrorMessage } from "@src/backendApi/err-formatting";

import type { V3Player } from "@neuromotion-inc/mightier-ts-sdk";

interface PlayerEnabledToggleProps {
  player: V3Player;
}

export function PlayerEnabledToggle(
  props: PlayerEnabledToggleProps,
): React.ReactElement {
  const { t } = useTranslation();

  const { player } = props;

  const { mutateAsync: patchPlayer } = usePatchPlayerMutation({
    onError: (err) => {
      // just gonna tell CX to use devtools to view the error.. lol
      console.error(err);

      notifications.show({
        title: t("playerDetails.updateFailed"),
        message: extractMaybeApiErrorMessage(err),
        color: "red",
      });
    },
    // reset so things like isDefaultValue work properly.
    onSettled: () => {
      form.reset();
    },
  });

  const form = useAppForm({
    defaultValues: {
      isActive: player.is_active,
    },
    onSubmit: async ({ value }) => {
      await patchPlayer({
        playerId: player.id,
        data: { is_active: value.isActive },
      });
    },
  });

  return (
    <Box>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <Fieldset legend={t("playerDetails.enabledOrDisable")}>
          <form.AppField
            name="isActive"
            children={(field) => {
              return <field.SwitchField label={t("playerDetails.isActive")} />;
            }}
          />
          <Group justify="flex-start" mt="md">
            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.isSubmitting,
                state.isDefaultValue,
              ]}
              children={([canSubmit, isSubmitting, isDefaultValue]) => (
                <form.SubmitButton
                  loading={isSubmitting}
                  disabled={!canSubmit || isDefaultValue}
                >
                  {t("accountDetails.submit")}
                </form.SubmitButton>
              )}
            />
          </Group>
        </Fieldset>
      </form>
    </Box>
  );
}
